import { JobsOptions } from 'bullmq/dist/esm/types'; // should find solution
import {
  CreateAchievementJob,
  CreateAchievementJobPayload,
} from './hub-jobs/achievement-jobs';
import {
  ReachedDailyLossJob,
  ReachedDailyLossJobPayload,
} from './hub-jobs/daily-loss';
import {
  ExecuteAddOnJob,
  ExecuteAddOnPayload,
} from './hub-jobs/execute-add-on-job';
import {
  FlushGroupedNotificationJob,
  FlushGroupedNotificationPayload,
} from './hub-jobs/flush-grouped-notification-job';
import {
  StockMarketJobsJob,
  StockMarketJobsPayload,
} from './hub-jobs/market-jobs';
import type { PaymentFailedPayload } from './hub-jobs/payment-failed-job';
import {
  PayoutBalanceOperationCompleteJob,
  PayoutBalanceOperationCompletePayload,
} from './hub-jobs/payout-balance-operation-complete';
import {
  RemoveTsUserJob,
  RemoveTsUserPayload,
} from './hub-jobs/remove-ts-user-job';
import {
  ResetDailyPauseJob,
  ResetDailyPauseJobsPayload,
} from './hub-jobs/reset-daily-pause-job';
import {
  AccountCreationCompletedJob,
  AccountCreationCompletedPayload,
  AccountCreationFailedJob,
  AccountCreationFailedPayload,
  AsyncCreateAccountJob,
  AsyncCreateAccountPayload,
  ContestAccountDeletedJob,
  CreateAccountJob,
  CreateAccountPayload,
  CreateContestAccountPayload,
  CreatePendingAccountsJob,
  ReachedDailyPauseJob,
  ReachedDailyPauseJobPayload,
  RecordAffiliationPurchaseJob,
  RecordAffiliationPurchasePayload,
  RetryAsyncAccountsCreationJob,
  SchedulerJob,
  TerminationJob,
  TerminationJobPayload,
  TradeTalkPickedPayload,
  TradingSystemJob,
  TradingSystemPayload,
} from './hub-jobs';

export enum BaseQueues {
  HubQueue = 'hub-queue',
  ContestQueue = 'contest-queue',
  AccountCreationQueue = 'account-creation-queue',
}

export enum HubQueueJobType {
  ReachedTarget = 'reached-target',
  ExecuteAddOn = 'execute-add-on',
  RecordAffiliationPurchase = 'record-affiliation-purchase',
  ResetDailyPause = 'reset-daily-pause',
  ReachedExpiration = 'reached-expiration',
  CleanOrders = 'clean-orders',
  CleanAdmin = 'clean-admin',
  ReachedDailyPause = 'reached-daily-pause',
  ReachedDailyLoss = 'reached-daily-loss',
  Terminated = 'terminated',
  WithdrawRequested = 'withdraw-requested',
  RestrictEarnings = 'restrict-earnings',
  ClearEarnings = 'clear-earnings',
  LiquidationAndExposure = 'liquidation-and-exposure',
  ResetExposure = 'reset-exposure',
  AboutToExpire = 'about-to-expire',
  CreateAchievement = 'create-achievement',
  CreditExpiration = 'credit-expiration',
  CreditExpirationWithin2Days = 'credit-expiration-within-2-days',
  CreditExpirationWithin7Days = 'credit-expiration-within-7-days',
  StockMarketJobs = 'stock-market-jobs',
  InactivityDays = 'inactivity-days',
  UpdateCurrencies = 'update-currencies',
  MaxLossTargetReached = 'max-loss-target-reached',
  TradeTalkMarkedAsPick = 'trade-talk-marked-as-pick',
  FlushGroupedNotification = 'flush-grouped-notification',
  PurchaseMade = 'purchase-made',
  PaymentFailed = 'payment-failed',
  PaymentSessionCanceler = 'payment-session-canceler',
  PayoutBalanceOperationComplete = 'payout-balance-operation-complete',
  RemoveTsUser = 'remove-ts-user',
}

export enum AccountCreationQueueJobType {
  CreateAccount = 'create-account',
  AsyncCreateAccount = 'async-create-account',
  CreatePendingAccounts = 'create-pending-accounts',
  AccountCreationCompleted = 'account-creation-completed',
  AccountCreationFailed = 'account-creation-failed',
  RetryAsyncAccountsCreation = 'retry-async-accounts-creation',
}

export enum ContestQueueJobType {
  CreateContestAccount = 'create-contest-account',
  CreatePendingContestAccounts = 'create-pending-contest-accounts',
  OpenContests = 'open-contests',
  ContestRegistrations = 'contest-registrations',
  ContestArchiver = 'contest-archiver',
  LastDayContest = 'last-day-contests',
  CloseContests = 'close-contests',
  SyncLeaderboards = 'sync-leaderboards',
  ResetDailyPauseContest = 'reset-daily-pause-contest',
  ReachedDailyPauseContest = 'reached-daily-pause-contest',
  ReachedDailyLossContest = 'reached-daily-loss-contest',
  ContestAccountTerminated = 'contest-account-terminated',
  ContestAccountDeleted = 'contest-account-deleted',
}

export type HubJobsPayload =
  | CreateAccountPayload
  | AsyncCreateAccountPayload
  | AccountCreationCompletedPayload
  | AccountCreationFailedPayload
  | CreateContestAccountPayload
  | PaymentFailedPayload
  | SchedulerJob
  | ContestAccountDeletedJob
  | TradingSystemPayload
  | ExecuteAddOnPayload
  | StockMarketJobsPayload
  | ResetDailyPauseJobsPayload
  | CreateAchievementJobPayload
  | ReachedDailyLossJobPayload
  | ReachedDailyPauseJobPayload
  | TerminationJobPayload
  | RecordAffiliationPurchasePayload
  | ContestJobsPayload
  | TradeTalkPickedPayload
  | FlushGroupedNotificationPayload
  | PayoutBalanceOperationCompletePayload
  | RemoveTsUserPayload
  | undefined;

type ContestJobsPayload =
  | CreateContestAccountPayload
  | SchedulerJob
  | ContestAccountDeletedJob
  | TradingSystemPayload
  | ResetDailyPauseJobsPayload
  | ReachedDailyLossJobPayload
  | ReachedDailyPauseJobPayload
  | TerminationJobPayload
  | undefined;

export type HubJobs = (
  | CreateAccountJob
  | AsyncCreateAccountJob
  | AccountCreationCompletedJob
  | AccountCreationFailedJob
  | RetryAsyncAccountsCreationJob
  | CreatePendingAccountsJob
  | TradingSystemJob
  | ExecuteAddOnJob
  | StockMarketJobsJob
  | ResetDailyPauseJob
  | CreateAchievementJob
  | ReachedDailyLossJob
  | ReachedDailyPauseJob
  | TerminationJob
  | RecordAffiliationPurchaseJob
  | FlushGroupedNotificationJob
  | PayoutBalanceOperationCompleteJob
  | RemoveTsUserJob
  | { type: HubQueueJobType; data?: any }
  | { type: AccountCreationQueueJobType; data?: any }
) & { options?: JobsOptions };

export type HubQueueJobs = Extract<HubJobs, { type: HubQueueJobType }>;
export type AccountCreationQueueJobs = Extract<
  HubJobs,
  { type: AccountCreationQueueJobType }
>;

export type ContestJobs = {
  type: ContestQueueJobType;
  data?: any;
  options?: JobsOptions;
};
